import { memo, useMemo } from 'react';
import { useInView } from 'react-intersection-observer';
import { Heading, Link } from '@appsumo/dorado-react';

import { DealImage } from '~/components/product';
import { DISCUSSION } from '~/lib/discussions/constants';
import { DiscussionType } from '~/lib/discussions/types';
import { getPlanTypeText } from '~/lib/product/utils';
import { sentenceCase } from '~/lib/util';
import { Deal } from '~/types/deal';

import { LTDMessaging } from '~/components/product/PricingCard';
import { BuyNow } from '~/components/product/PricingCard/BuyNow';
import { ListingTypeLogo } from '~/components/product/ListingTypeLogo';
import { DealListingType } from '~/components/sku/types';

import ProductDetailRating from './ProductDetailRating';

interface AggregateSidebarPricingCardProps {
  deal: Deal;
  discussionType: DiscussionType;
}

function PricingCard({
  deal,
  discussionType,
}: Readonly<AggregateSidebarPricingCardProps>) {
  const PDPUrl = `/products/${deal.slug}/`;
  const subHeader = deal.products[0]?.story?.subheader || '';

  const { ref, inView } = useInView({
    threshold: 0,
  });

  const planTypeText = useMemo(
    () => sentenceCase(getPlanTypeText(deal?.unique_plan_types)),
    [deal],
  );
  const isReviews = discussionType === DISCUSSION.REVIEWS;

  return (
    <div className="max-w-[400px] justify-center max-lg:mx-auto max-lg:text-center lg:justify-start">
      <Link href={PDPUrl} target="_blank">
        <DealImage
          deal={deal as any}
          priority={true}
          width={400}
          height={225}
          className="rounded"
        />
      </Link>
      {/* content */}
      <div className="mt-4 flex flex-col items-center gap-y-2 lg:items-start">
        {!deal.is_info && (
          <ListingTypeLogo
            type={deal.listing_type as DealListingType}
            className="hidden md:flex"
          />
        )}
        {/* title + plan type */}
        <div className="flex flex-col">
          <Link href={PDPUrl} target="_blank">
            <Heading.H1>{deal.public_name}</Heading.H1>
          </Link>
        </div>
        {!deal.is_info && (
          <>
            {planTypeText === 'Lifetime deal' && !deal?.is_free ? (
              <LTDMessaging />
            ) : (
              <Heading.H6 className="text-forest">{planTypeText}</Heading.H6>
            )}
            {subHeader && (
              <p data-testid="pricing-card-subheader">{subHeader}</p>
            )}
          </>
        )}
        <ProductDetailRating
          dealReview={deal.deal_review}
          reviewUrl={`${PDPUrl}reviews/`}
          PDPUrl={PDPUrl}
          showDealRatings={!isReviews}
        />

        <div ref={ref} className="w-full">
          <BuyNow
            deal={deal}
            inView={inView}
            isPricingCard
            hideStickyBuyButtonSize="lg"
          />
        </div>
      </div>
    </div>
  );
}

export default memo(PricingCard);
